<template>
  <div class="image-comparison-container" :class="{ 'full-slide': fullSlide }">
    <section class="comparison-section" ref="sectionRef">
      <input 
        class="slider" 
        type="range" 
        min="0" 
        max="100" 
        v-model="sliderValue"
        @input="handleSliderInput"
        ref="sliderRef"
      />
      <div class="drag" :style="{ left: `${compareValue}%` }">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="size-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      </div>
      <img
        :src="beforeImage"
        :alt="beforeAlt || 'Before image'"
        class="before-image"
      />
      <img
        :src="afterImage"
        :alt="afterAlt || 'After image'"
        class="after-image"
        :style="{ clipPath: `inset(0 0 0 ${compareValue}%)` }"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

interface Props {
  beforeImage: string
  afterImage: string
  beforeAlt?: string
  afterAlt?: string
  fullSlide?: boolean
  initialValue?: number
}

const props = withDefaults(defineProps<Props>(), {
  beforeAlt: 'Before image',
  afterAlt: 'After image',
  fullSlide: false,
  initialValue: 50
})

const sliderValue = ref(props.initialValue)
const compareValue = ref(props.initialValue)
const sectionRef = ref<HTMLElement>()
const sliderRef = ref<HTMLInputElement>()

const dragTranslate = computed(() => {
  return `calc(-50% + (${compareValue.value} * 1cqi)) -50%`
})

const handleSliderInput = () => {
  compareValue.value = Number(sliderValue.value)
}

// Simple fallback for all browsers - just use the input directly
onMounted(() => {
  // Initialize the comparison value
  compareValue.value = Number(sliderValue.value)
})
</script>

<style scoped>
.image-comparison-container {
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.image-comparison-container.full-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  max-width: none;
  z-index: 1;
}

.comparison-section {
  position: relative;
  margin: 0 auto;
  border: 2px solid color-mix(in oklch, canvas, canvasText);
  container-type: inline-size;
  width: 100%;
  aspect-ratio: 4 / 3;
  box-shadow: 0 1rem 2rem -1rem light-dark(hsl(0 0% 0% / 0.6), hsl(0 0% 0% / 1));
  overflow: hidden;
}

.full-slide .comparison-section {
  width: 100vw;
  height: 100vh;
  aspect-ratio: unset;
  border: none;
  box-shadow: none;
}

.full-slide .comparison-section img {
  object-fit: contain;
}

.comparison-section img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  inset: 0;
}

.before-image {
  z-index: 1;
}

.after-image {
  z-index: 2;
}

.slider {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: grab;
  z-index: 10;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  -webkit-appearance: none;
  appearance: none;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 100%;
  cursor: grab;
  background: transparent;
}

.slider::-moz-range-thumb {
  width: 20px;
  height: 100%;
  cursor: grab;
  border: none;
  background: transparent;
  -moz-appearance: none;
}

.slider::-moz-range-track {
  background: transparent;
  border: none;
}

.slider:active {
  cursor: grabbing;
}

.drag {
  height: 40px;
  aspect-ratio: 1;
  border-radius: 50%;
  position: absolute;
  z-index: 5;
  top: 50%;
  transform: translate(-50%, -50%) rotate(90deg);
  color: #fff;
  background: #000;
  border: 2px solid hsl(0 0% 40%);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease, left 0.1s ease;
}

.comparison-section:active .drag {
  background: hsl(0 0% 20%);
  opacity: 0;
}

.drag svg {
  width: 20px;
  height: 20px;
}

/* CSS Scroll-driven animation support */
@supports (animation-timeline: scroll()) and (animation-range: entry) {
  @property --compare {
    inherits: true;
    syntax: '<integer>';
    initial-value: 0;
  }

  :root {
    timeline-scope: --control;
  }

  .comparison-section {
    animation: sync both linear reverse;
    animation-timeline: --control;
    animation-range: contain;
  }

  .slider::-webkit-slider-thumb {
    view-timeline: --control inline;
  }

  @keyframes sync {
    to {
      --compare: 100;
    }
  }

  .drag {
    translate: calc(-50% + (var(--compare, 0) * 1cqi)) -50%;
  }

  .after-image {
    clip-path: inset(0 0 0 calc(var(--compare, 0) * 1%)) !important;
  }
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .comparison-section {
    border-color: color-mix(in oklch, canvas, canvasText 30%);
  }
}

:global(.dark) .comparison-section {
  border-color: color-mix(in oklch, canvas, canvasText 30%);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .comparison-section {
    aspect-ratio: 3 / 4;
  }
  
  .full-slide .comparison-section {
    aspect-ratio: unset;
  }
  
  .drag {
    height: 35px;
  }
  
  .drag svg {
    width: 18px;
    height: 18px;
  }
}

@media (max-width: 480px) {
  .drag {
    height: 30px;
  }
  
  .drag svg {
    width: 16px;
    height: 16px;
  }
}
</style>
